import type { components } from "@/generated/openapi-types";

type Dashboard = components["schemas"]["AdminDashboard"];

/**
 * THE GOALS. Edit this file to change what the console is aiming at.
 *
 * It is the only place a target lives. There is no goals table, no admin
 * screen for editing them, and that is deliberate: a milestone is a decision
 * about what the product is trying to be this quarter, it changes a handful
 * of times a year, and it belongs in a commit with a reason attached rather
 * than in a text field somebody nudged on a Tuesday. `current` is always
 * DERIVED — never stored — so a goal cannot drift out of agreement with the
 * numbers it claims to track.
 *
 * The model is deliberately the same one the sibling product's console
 * uses: a `Group` of `Goal`s, each with a label, a target, a derived current
 * and an optional note. Same shape, same maths, so the two consoles stay
 * legible to the same person.
 *
 * Every figure counts real accounts only — the dashboard queries exclude
 * `users.is_internal`, so a milestone cannot be reached with our own logins.
 */

export type Unit = "count" | "percent";

export interface Goal {
  label: string;
  target: number;
  current: number;
  unit: Unit;
  /** One line saying what the number means, when it is not obvious. */
  note?: string;
}

export interface Group {
  category: string;
  blurb: string;
  goals: Goal[];
}

export function buildGroups(d: Dashboard): Group[] {
  const users = d.users.total;
  const engagement = d.engagement;
  const cafes = d.cafes;

  const activationRate =
    users > 0 ? (engagement.usersWithReview / users) * 100 : 0;
  const weeklyActiveRate =
    users > 0 ? (engagement.activeUsersLast7Days / users) * 100 : 0;
  const photoRate =
    d.reviews.total > 0
      ? (engagement.reviewsWithPhoto / d.reviews.total) * 100
      : 0;

  return [
    {
      category: "People",
      blurb: "Real accounts. Our own logins are flagged internal and excluded.",
      goals: [
        { label: "First 10 people", target: 10, current: users, unit: "count" },
        { label: "100 registered", target: 100, current: users, unit: "count" },
        {
          label: "25 weekly active",
          target: 25,
          current: engagement.activeUsersLast7Days,
          unit: "count",
          note: "Posted a review, a check-in or a photo in the last 7 days.",
        },
        {
          label: "Half of everyone active weekly",
          target: 50,
          current: weeklyActiveRate,
          unit: "percent",
        },
        {
          label: "500 registered",
          target: 500,
          current: users,
          unit: "count",
        },
      ],
    },
    {
      category: "The map",
      blurb:
        "A cafe only counts once a person has been and confirmed it serves matcha.",
      goals: [
        {
          label: "50 cafes confirmed",
          target: 50,
          current: cafes.withConfirmedVerdict,
          unit: "count",
          note: "Verdict confirmed — at least one person who went.",
        },
        {
          label: "250 cafes confirmed",
          target: 250,
          current: cafes.withConfirmedVerdict,
          unit: "count",
        },
        {
          label: "200 cafes with a rating",
          target: 200,
          current: cafes.withScore,
          unit: "count",
          note: "Has a computed score, so the list can rank it.",
        },
        {
          label: "3 cities live",
          target: 3,
          current: cafes.citiesLaunched,
          unit: "count",
          note: "Launched, not merely covered by the OSM import.",
        },
        {
          label: "10 cities live",
          target: 10,
          current: cafes.citiesLaunched,
          unit: "count",
        },
      ],
    },
    {
      category: "What people leave behind",
      blurb: "The corpus is only as good as what people bother to record.",
      goals: [
        {
          label: "250 reviews",
          target: 250,
          current: d.reviews.total,
          unit: "count",
        },
        {
          label: "1,000 reviews",
          target: 1000,
          current: d.reviews.total,
          unit: "count",
        },
        {
          label: "Two thirds of reviews carry a photo",
          target: 66,
          current: photoRate,
          unit: "percent",
          note: "A photo is what makes a review readable to a stranger.",
        },
        {
          label: "Half of people have reviewed",
          target: 50,
          current: activationRate,
          unit: "percent",
          note: "The activation number: signed up AND said something.",
        },
        {
          label: "500 check-ins",
          target: 500,
          current: d.checkIns.total,
          unit: "count",
        },
      ],
    },
  ];
}
